'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const services = [
  { id: 'coaching', label: '1-on-1 Coaching', icon: 'SparklesIcon' as const },
  { id: 'performance', label: 'Live Performance', icon: 'MusicalNoteIcon' as const },
  { id: 'workshop', label: 'Corporate Workshop', icon: 'UserGroupIcon' as const },
  { id: 'writing', label: 'Writing Collaboration', icon: 'PencilSquareIcon' as const },
];

const faqs = [
  {
    q: 'How does the 1-on-1 coaching work?',
    a: 'Sessions are 60 minutes, conducted over video call or in-person in Pune. We begin with a complimentary 20-minute discovery call to understand your goals. Most clients see significant shifts within 6–8 sessions.',
  },
  {
    q: 'Can Kavita perform at private events outside Pune?',
    a: 'Absolutely. Kavita performs nationally and has performed internationally in London, Singapore, and Dubai. Travel and accommodation are coordinated through her management team.',
  },
  {
    q: 'What is the coaching fee structure?',
    a: 'Individual sessions start at ₹8,000. Monthly packages (4 sessions) are available at ₹28,000. Corporate workshop pricing is customized based on group size and duration.',
  },
  {
    q: 'How far in advance should I book?',
    a: "For coaching, slots are typically available within 2 weeks. For performances, we recommend booking 6–8 weeks in advance. Corporate workshops require a minimum of 4 weeks' notice.",
  },
];

export default function ContactSection() {
  const [selected, setSelected] = useState<string>('coaching');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.will-animate');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
          interest: selected,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitError('Unable to send message right now. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 lg:py-40" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-20 scroll-reveal will-animate">
          <span className="section-label mb-4">Let&apos;s Connect</span>
          <h2
            className="font-fraunces font-light leading-tight"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 5.5rem)' }}
          >
            Begin your <br />
            <em className="italic text-gold-shimmer">transformation.</em>
          </h2>
          <p className="text-muted text-base leading-relaxed max-w-md mt-6">
            Whether you&apos;re looking to find your voice on stage, in print, or in life — Kavita
            is ready to guide you.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left — Form */}
          <div className="lg:col-span-7 scroll-reveal-left will-animate">
            <div
              className="rounded-[40px] p-8 lg:p-12"
              style={{ background: '#F2E8DF', border: '1px solid var(--border)' }}
            >
              {/* Service selector */}
              <div className="mb-10">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted mb-4">
                  I&apos;m interested in
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSelected(s.id)}
                      className={`flex items-center gap-3 p-4 rounded-2xl text-left transition-all duration-300 ${
                        selected === s.id
                          ? 'bg-charcoal text-cream shadow-luxury'
                          : 'bg-white/70 text-charcoal hover:bg-white'
                      }`}
                    >
                      <Icon
                        name={s.icon}
                        size={18}
                        className={selected === s.id ? 'text-gold-light' : 'text-muted'}
                        variant="solid"
                      />
                      <span className="text-xs font-semibold">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label className="text-10 tracking-widest uppercase text-muted font-semibold block mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ananya Sharma"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="text-10 tracking-widest uppercase text-muted font-semibold block mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="ananya@example.com"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="text-10 tracking-widest uppercase text-muted font-semibold block mb-2">
                    Your Message
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell Kavita about what you're looking for..."
                    rows={4}
                    className="form-input resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-gold w-full justify-center text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      Sending...
                      <Icon name="PaperAirplaneIcon" size={16} />
                    </>
                  ) : submitted ? (
                    <>
                      <Icon name="CheckIcon" size={16} />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      Send Message
                      <Icon name="PaperAirplaneIcon" size={16} />
                    </>
                  )}
                </button>
                {submitError && <p className="text-sm text-red-600">{submitError}</p>}
              </form>
            </div>
          </div>

          {/* Right — Contact info + FAQ */}
          <div className="lg:col-span-5 scroll-reveal-right will-animate flex flex-col gap-8">
            {/* Contact info */}
            <div className="rounded-[32px] p-8" style={{ background: 'var(--charcoal)' }}>
              <p className="section-label mb-6" style={{ color: 'var(--gold)' }}>
                Direct Contact
              </p>
              <div className="space-y-5">
                {[
                  {
                    icon: 'EnvelopeIcon' as const,
                    label: 'Email',
                    value: 'kavita.t.official@gmail.com',
                  },
                  { icon: 'PhoneIcon' as const, label: 'Phone', value: '+91 9822813182' },
                  { icon: 'MapPinIcon' as const, label: 'Based in', value: 'Pune, Maharashtra' },
                  { icon: 'ClockIcon' as const, label: 'Response', value: 'Within 24 hours' },
                ].map((c) => (
                  <div key={c.label} className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'rgba(184,150,110,0.15)',
                        border: '1px solid rgba(184,150,110,0.2)',
                      }}
                    >
                      <Icon name={c.icon} size={16} className="text-gold" />
                    </div>
                    <div>
                      <p className="text-10 tracking-widest uppercase text-cream/40">{c.label}</p>
                      <p className="text-sm text-cream font-medium mt-0.5">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-muted mb-4">
                Common Questions
              </p>
              <div className="space-y-0">
                {faqs.map((faq, i) => (
                  <div key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full py-5 flex items-center justify-between text-left gap-4 group"
                    >
                      <span className="text-sm font-semibold text-charcoal group-hover:text-gold transition-colors">
                        {faq.q}
                      </span>
                      <Icon
                        name="ChevronDownIcon"
                        size={16}
                        className={`text-muted flex-shrink-0 transition-transform duration-300 ${
                          openFaq === i ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div className={`faq-content ${openFaq === i ? 'open' : ''}`}>
                      <p className="text-sm text-muted leading-relaxed pb-5">{faq.a}</p>
                    </div>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid var(--border)' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
